"use client";

import * as React from "react";
import useTranslation from "next-translate/useTranslation";
import { PopupInfo } from "components/PopupInfo/PopupInfo";
import { cn } from "@/lib/utils";
import { homeCardVariants } from "../foundations/home-variants";
import { HomeLoadingStateV2 } from "./HomeLoadingStateV2";

export interface HomeMetricCardV2Props {
  title: string;
  value: number;
  refValue?: number;
  filled?: boolean;
  percent?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  popupInfo?: string;
}

export function HomeMetricCardV2({
  title,
  value,
  refValue,
  filled,
  percent,
  icon,
  loading,
  popupInfo
}: HomeMetricCardV2Props) {
  const { t } = useTranslation("common");
  const visualIcon = React.isValidElement<{ className?: string }>(icon)
    ? React.cloneElement(icon, {
        className: cn(icon.props.className, "h-10 w-10"),
      })
    : icon;

  return (
    <div className="relative">
      <div
        className={cn(
          homeCardVariants({ variant: "metric" }),
          "min-h-[150px] border-2 p-4",
          filled && "bg-slate-50 dark:bg-zinc-800/80"
        )}
      >
        <div className="flex h-full min-h-[118px] flex-col items-center justify-center gap-3 text-center">
          {visualIcon ? (
            <div className="flex text-indigo-500 dark:text-indigo-300">
              {visualIcon}
            </div>
          ) : null}
          <div className="space-y-1">
            <div className="text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
              {loading ? (
                <HomeLoadingStateV2 inline compact />
              ) : !percent ? (
                value
              ) : (
                `${value}%`
              )}
              {refValue || refValue === 0 ? (
                <span className="ml-1 text-sm font-medium text-slate-500 dark:text-zinc-400">
                  {t("de")} {refValue}
                </span>
              ) : null}
            </div>
            <p className="mx-auto max-w-[15rem] text-xs font-medium leading-snug text-slate-500 dark:text-zinc-400">
              {title}
            </p>
          </div>
        </div>
      </div>
      {popupInfo && (
        <div className="absolute right-2 top-2">
          <PopupInfo className="ml-2 self-center" label={popupInfo} placement="bottomEnd" />
        </div>
      )}
    </div>
  );
}
